import { useState } from 'react'

type Os = 'win' | 'mac'

/**
 * Setup, troubleshooting, and how to update 文殊 itself.
 *
 * Written for the person who is stuck right now, which is why it is short and the
 * commands are the whole content: every failure this page covers actually happened
 * during the first two days of use — the wrong shell, a missing config file, an
 * approval prompt that never appeared, a browser left on stale JavaScript.
 *
 * It does not reproduce the full manual. The hub serves that as a file, so the one
 * long document has a single source rather than a copy here that drifts from it.
 */
export function Help({ onBack }: { onBack: () => void }) {
  const [os, setOs] = useState<Os>(
    typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform) ? 'mac' : 'win',
  )
  const hub = location.origin
  const bin = os === 'win' ? '"$HOME\\crosssession\\crosssession.mjs"' : '~/crosssession/crosssession.mjs'

  const Cmd = ({ children }: { children: string }) => <pre className="cmd">{children}</pre>

  return (
    <div className="wrap">
      <button className="text" onClick={onBack} style={{ marginBottom: 8 }}>
        ← プロジェクト一覧
      </button>

      <h1>文殊の使い方</h1>
      <p className="muted" style={{ marginTop: -4 }}>
        文殊 / もんじゅ / モンジュ / もんちゃん / Manjushri — どれで呼んでも同じものです。
        Claude にもこの名前で伝わります。
      </p>

      <div className="card">
        <h2>これは何か</h2>
        <p style={{ marginTop: 0 }}>
          会社の違う相手と、人と AI が同じ場所でやりとりするための仕組みです。これまで
          「相手の Claude が質問 → 人が転送 → こちらの Claude が回答 → 人が返信」とやっていた
          往復を、直接つなぎます。
        </p>
        <p style={{ marginBottom: 0 }}>
          <strong>判断は人間がします。</strong>AI は質問・回答・要約の下書きを運ぶ役です。
          社外に出るものは、出す前に必ずあなたが確認します。
        </p>
      </div>

      <div className="card">
        <h2>導入</h2>
        <div className="help-os" role="group" aria-label="OS">
          <button
            className={os === 'win' ? 'primary' : ''}
            aria-current={os === 'win'}
            onClick={() => setOs('win')}
          >
            Windows
          </button>
          <button
            className={os === 'mac' ? 'primary' : ''}
            aria-current={os === 'mac'}
            onClick={() => setOs('mac')}
          >
            Mac
          </button>
        </div>

        {os === 'win' && (
          <p className="muted" style={{ marginTop: 0 }}>
            <strong>PowerShell</strong> を開いてください。コマンドプロンプト（<code>cmd</code>）では{' '}
            <code>$HOME</code> が展開されず失敗します。cmd を使う場合は <code>$HOME</code> を{' '}
            <code>%USERPROFILE%</code> に読み替えてください。
          </p>
        )}

        <h3>1. Node.js があるか確認する</h3>
        <Cmd>node -v</Cmd>
        <p className="muted">
          <code>v20</code> 以上なら OK。無ければ{' '}
          <a href="https://nodejs.org/" target="_blank" rel="noreferrer noopener">
            nodejs.org
          </a>{' '}
          の LTS を入れて、ターミナルを開き直してください。
        </p>

        <h3>2. 文殊をダウンロードする</h3>
        <Cmd>
          {os === 'win'
            ? `New-Item -ItemType Directory -Force "$HOME\\crosssession" | Out-Null\nInvoke-WebRequest -Uri ${hub}/download/crosssession.mjs \`\n  -OutFile "$HOME\\crosssession\\crosssession.mjs"`
            : `mkdir -p ~/crosssession\ncurl -fsSL ${hub}/download/crosssession.mjs \\\n  -o ~/crosssession/crosssession.mjs`}
        </Cmd>
        <p className="muted">
          ファイルは 1 つだけです。インストールもビルドも要りません。{' '}
          <a href="/download/crosssession.mjs.sha256">SHA-256</a> で中身を照合できます。
        </p>

        <h3>3. 参加する</h3>
        <Cmd>
          {os === 'win'
            ? `node ${bin} join \`\n  --hub ${hub} \`\n  --code （届いた招待コード） \`\n  --name "あなたの表示名"`
            : `node ${bin} join \\\n  --hub ${hub} \\\n  --code （届いた招待コード） \\\n  --name "あなたの表示名"`}
        </Cmd>
        <p className="muted">
          参加の条件が 4 項目表示されます。読んで <code>y</code> を入力してください。
        </p>

        <h3>4. 案件のフォルダで有効にする</h3>
        <Cmd>{`cd （案件のフォルダ）\nnode ${bin} setup`}</Cmd>
        <p className="muted" style={{ marginBottom: 0 }}>
          最後に Claude Code を再起動します。<code>/mcp</code> に <code>manjushri</code> が出れば
          完了です。
        </p>
      </div>

      <div className="card">
        <h2>更新のしかた</h2>
        <p style={{ marginTop: 0 }}>
          手順 2 のダウンロードをもう一度実行して、同じ場所に上書きするだけです。パスが
          変わらないので <code>setup</code> のやり直しは要りません。Claude Code は再起動して
          ください。
        </p>
        <Cmd>
          {os === 'win'
            ? `Invoke-WebRequest -Uri ${hub}/download/crosssession.mjs \`\n  -OutFile "$HOME\\crosssession\\crosssession.mjs"`
            : `curl -fsSL ${hub}/download/crosssession.mjs -o ~/crosssession/crosssession.mjs`}
        </Cmd>
        <p className="muted" style={{ marginBottom: 0 }}>
          この画面が古くなると、上部に「新しいバージョンが公開されています」と出ます。出たら
          再読み込みしてください。出ないうちは最新です。
        </p>
      </div>

      <div className="card">
        <h2>うまく動かないとき</h2>
        <p className="muted" style={{ marginTop: 0 }}>
          まず状態を見ます。
        </p>
        <Cmd>{`node ${bin} status`}</Cmd>

        <div style={{ overflowX: 'auto' }}>
          <table className="m3">
            <thead>
              <tr>
                <th style={{ minWidth: 200 }}>症状</th>
                <th>確認すること</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <code>cs_</code> のツールが出てこない
                </td>
                <td>
                  そのフォルダで <code>setup</code> を実行したか。再起動時に{' '}
                  <code>.mcp.json</code> の確認を<strong>許可</strong>したか。
                  <code>/mcp</code> に <code>manjushri</code> が出るか。
                  <br />
                  デスクトップアプリで何も聞かれない場合は、Claude Code を終了してから{' '}
                  <code>setup --mcp-scope user</code> をやり直してください。全プロジェクトで
                  有効になり、確認そのものが不要になります。
                </td>
              </tr>
              <tr>
                <td>
                  <code>Cannot find module …$HOME…</code>
                </td>
                <td>
                  コマンドプロンプト（cmd）で実行しています。PowerShell を使うか、
                  <code>$HOME</code> を <code>%USERPROFILE%</code> に読み替えてください。
                </td>
              </tr>
              <tr>
                <td>
                  <code>先に join してください</code>
                </td>
                <td>
                  設定がまだです。設定ファイルは<strong>プログラムとは別のフォルダ</strong>
                  （<code>.claude/crosssession/.env</code>）にできます。プロジェクトの
                  オーナーは <code>join</code> ではなく <code>configure --token</code> を
                  使います。
                </td>
              </tr>
              <tr>
                <td>
                  <code>consent_required</code>
                </td>
                <td>ダウンロードしたファイルが古いです。更新してから再実行してください。</td>
              </tr>
              <tr>
                <td>質問が届かない・チャットが更新されない</td>
                <td>
                  <code>setup</code> 後に Claude Code を<strong>再起動</strong>したか。
                  ブラウザは再読み込みしてください。画面上部に「リアルタイム更新に接続できて
                  いません」と出ている場合は、通信が塞がれています。
                </td>
              </tr>
              <tr>
                <td>どのプロジェクトか特定できません</td>
                <td>
                  そのフォルダで <code>link --room （room_id）</code> を実行してください。
                  <code>.crosssession</code> ができます。これはコミットして構いません。
                </td>
              </tr>
              <tr>
                <td>招待コードが使えない</td>
                <td>
                  一度きり・期限つきです。発行者に再発行を依頼してください。CLI で参加済みの
                  場合、ブラウザには<strong>トークン</strong>で入ります（下記）。
                </td>
              </tr>
              <tr>
                <td>ブラウザに入れない</td>
                <td>
                  参加画面の「既存のトークンで入る」に、設定ファイルの <code>CS_TOKEN=</code> の
                  後ろの値を貼り付けてください。
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="card">
        <h2>用語</h2>
        <div className="kv">
          <span className="muted">共有待ち</span>
          <span>
            あなたの Claude が出した要約や仕様変更の下書き。
            <strong>あなたが共有するまで相手には見えません。</strong>
            ここで決めるのは「出すか出さないか」だけで、内容の良し悪しの判断ではありません。
          </span>
          <span className="muted">責任領域</span>
          <span>
            実装 / 要件 / 期限 / 契約 / 検収。<strong>何の最終判断をする人か</strong>を表します。
            権限（オーナー・メンバー・閲覧）とは別物で、上下関係でもありません。
          </span>
          <span className="muted">宛先</span>
          <span>
            「本人」と「本人の Claude」を分けて指定できます。判断が要ることは本人宛に、
            事実確認は相手の Claude 宛に投げると待ち時間が減ります。
          </span>
          <span className="muted">活動シグナル</span>
          <span>
            だいたいいつ作業したか、だいたいいつ共有したかだけを日単位で共有します。
            <strong>作業内容は含みません。</strong>
            「作業は進んでいるが共有がない」状態が分かるので、共有を依頼できます。
          </span>
        </div>
      </div>

      <div className="card">
        <h2>もっと詳しく</h2>
        <p className="muted" style={{ marginTop: 0 }}>
          配布用の手順書とセキュリティ基準です。新しく参加する方にはこれを渡してください。
        </p>
        <div className="row">
          <a href="/docs/setup-member.md" download>
            セットアップ手順書 (Markdown)
          </a>
          <a href="/docs/security-criteria.md" download>
            セキュリティ基準
          </a>
          <a href="/download/crosssession.mjs" download>
            crosssession.mjs
          </a>
        </div>
      </div>
    </div>
  )
}
